import React from "react";

import { Box, Typography } from "@mui/material";

import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";

import ArtworkFrame from "./ArtworkFrame";

import { tokens } from "../../theme/galleryTheme";

const clamp = (lines) => ({
  display: "-webkit-box",
  WebkitLineClamp: lines,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
});

const renderTextWithBreaks = (text) => {
  if (!text) return null;

  const parts = String(text).split(/<br\s*\/?>/gi);

  return parts.map((part, index) => (
    <React.Fragment key={index}>
      {part}
      {index < parts.length - 1 && <br />}
    </React.Fragment>
  ));
};

function CardLink({ href, onClick, children, sx, ...rest }) {
  return (
    <Box
      component="a"
      href={href}
      onClick={onClick}
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        color: "inherit",
        textDecoration: "none",

        "&:hover .lg-frame-img": {
          transform: "scale(1.02)",
        },

        "&:hover .lg-card-title, &:focus-visible .lg-card-title": {
          textDecoration: "underline",
          textDecorationThickness: "1px",
          textUnderlineOffset: "4px",
        },

        ...sx,
      }}
      {...rest}
    >
      {children}
    </Box>
  );
}

/**
 * Auction or exhibition:
 * cover image, type label, title, dates.
 */
export function EventCard({
  href,
  onClick,
  image,
  imageAlt,
  eyebrow,
  title,
  meta,
  ratio = "4 / 5",
  compact = false,
}) {
  return (
    <CardLink href={href} onClick={onClick}>
      <ArtworkFrame
        src={image}
        alt={imageAlt || title || ""}
        ratio={ratio}
      />

      <Box
        sx={{
          display: "grid",
          gap: 1,
          pt: 2,
        }}
      >
        {eyebrow && (
          <Typography variant="overline" component="p">
            {eyebrow}
          </Typography>
        )}

        <Typography
          className="lg-card-title"
          variant="h3"
          component="h3"
          sx={{
            color: tokens.color.ink,
            ...clamp(3),

            ...(compact
              ? {
                  fontSize: {
                    xs: "1.15rem",
                    md: "1.35rem",
                  },
                  lineHeight: 1.25,
                }
              : {}),
          }}
        >
          {title}
        </Typography>

        {meta && (
          <Typography variant="body2" component="p">
            {meta}
          </Typography>
        )}
      </Box>
    </CardLink>
  );
}

const STATUS_TONES = {
  positive: {
    color: "#2E7D32",
    bg: "#F1F8E9",
  },

  negative: {
    color: tokens.color.live,
    bg: "#FFF5F5",
  },

  neutral: {
    color: tokens.color.ink,
    bg: tokens.color.ivory,
  },
};

export function ArtworkCard({
  href,
  onClick,
  image,
  artist,
  title,
  eyebrow,
  meta,
  imageAlt,
  favorite = false,
  status,
  favoriteOnClick,
  favoriteAriaLabel,
  action,
  compact = false,
  metaSize,
  metaWeight,
}) {
  const alt =
    imageAlt || [artist, title].filter(Boolean).join(", ");

  const tone = status
    ? STATUS_TONES[status.tone] || STATUS_TONES.neutral
    : null;

  const hasFooter =
    Boolean(meta) || Boolean(status && status.label);

  return (
    <CardLink
      href={href}
      onClick={onClick}
      sx={{
        bgcolor: "#fff",

        // Clean gallery-style card.
        // No border — only a very subtle shadow.
        borderRadius: {
          xs: "4px",
          md: "6px",
        },

        overflow: "hidden",

        boxShadow: "0 1px 4px rgba(0, 0, 0, 0.06)",

        transition:
          "box-shadow 0.2s ease, transform 0.2s ease",

        "&:hover": {
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.10)",
        },
      }}
    >
      {/* IMAGE */}
      <ArtworkFrame
        src={image}
        alt={alt}
        ratio="4 / 5"
        mat="#fff"
        padding={0}
      >
        {(favorite || favoriteOnClick) && (
          <FavoriteBadge
            onClick={favoriteOnClick}
            ariaLabel={
              favoriteAriaLabel || "In your favourites"
            }
            active={favorite}
          />
        )}
      </ArtworkFrame>

      {/* DETAILS */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minWidth: 0,

          bgcolor: "#fff",

          pt: {
            xs: 0.75,
            md: 1,
          },

          px: {
            xs: 1,
            md: 1.5,
          },

          pb: {
            xs: 1,
            md: 1.5,
          },
        }}
      >
        {/* EYEBROW */}
        <Typography
          variant="overline"
          component="p"
          title={eyebrow || undefined}
          sx={{
            ...clamp(1),

            minHeight: "1.5em",

            wordBreak: "normal",
            overflowWrap: "normal",
            hyphens: "none",

            ...(compact
              ? {
                  fontSize: "0.72rem",
                  letterSpacing: "0.14em",
                }
              : {}),
          }}
        >
          {eyebrow || "\u00A0"}
        </Typography>

        {/* ARTIST */}
        <Typography
          className="lg-card-title"
          variant="h4"
          component="h3"
          title={artist || undefined}
          sx={{
            color: tokens.color.ink,

            mt: 0.5,

            ...clamp(2),

            minHeight: "2.64em",

            wordBreak: "normal",
            overflowWrap: "normal",
            hyphens: "none",

            ...(compact
              ? {
                  fontSize: {
                    xs: "1.08rem",
                    md: "1.22rem",
                  },

                  lineHeight: 1.25,
                }
              : {}),
          }}
        >
          {renderTextWithBreaks(artist)}
        </Typography>

        {/* TITLE */}
        <Typography
          variant="body2"
          component="p"
          title={title || undefined}
          sx={{
            fontStyle: "italic",

            mt: 0.5,

            ...clamp(2),

            minHeight: "3.2em",

            wordBreak: "break-word",

            ...(compact
              ? {
                  fontSize: "0.9rem",
                  lineHeight: 1.35,
                }
              : {}),
          }}
        >
          {renderTextWithBreaks(title)}
        </Typography>

        {/* FOOTER */}
        {hasFooter && (
          <Box
            sx={{
              mt: "auto",

              pt: 0.75,

              display: "grid",

              gap: {
                xs: 0.75,
                md: 1,
              },
            }}
          >
            {/* META / PRICE */}
            {meta && (
              <Typography
                variant="body2"
                component="p"
                sx={{
                  color: tokens.color.ink,

                  fontWeight: metaWeight || 500,

                  ...clamp(1),

                  wordBreak: "break-word",

                  fontSize:
                    metaSize ||
                    (compact ? "0.78rem" : undefined),
                }}
              >
                {meta}
              </Typography>
            )}

            {/* STATUS */}
            {status && status.label && (
              <Typography
                component="p"
                sx={{
                  px: 1.25,
                  py: 0.75,

                  fontSize: compact
                    ? "0.74rem"
                    : "0.8125rem",

                  fontWeight: 600,

                  lineHeight: 1.4,

                  minHeight: "calc(2.8em + 12px)",

                  display: "flex",
                  alignItems: "center",

                  color: tone.color,
                  bgcolor: tone.bg,

                  // Ito lang ang accent line.
                  borderLeft: `3px solid ${tone.color}`,

                  wordBreak: "break-word",

                  borderRadius: "2px",
                }}
              >
                {status.label}
              </Typography>
            )}

            {/* ACTION */}
            {action && (
              <Box sx={{ pt: 0.25 }}>
                {action}
              </Box>
            )}
          </Box>
        )}
      </Box>
    </CardLink>
  );
}

function FavoriteBadge({
  onClick,
  ariaLabel = "In your favourites",
  active = true,
}) {
  const sx = {
    position: "absolute",

    top: {
      xs: 6,
      md: 10,
    },

    right: {
      xs: 6,
      md: 10,
    },

    width: {
      xs: 30,
      md: 34,
    },

    height: {
      xs: 30,
      md: 34,
    },

    borderRadius: "50%",

    bgcolor: "rgba(255,255,255,0.94)",

    boxShadow: "0 1px 3px rgba(0,0,0,0.12)",

    display: "flex",
    alignItems: "center",
    justifyContent: "center",

    color: tokens.color.live,

    zIndex: 1,
  };

  if (onClick) {
    return (
      <Box
        component="button"
        type="button"
        aria-label={ariaLabel}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          onClick(event);
        }}
        sx={{
          ...sx,

          border: 0,
          padding: 0,
          cursor: "pointer",

          "&:hover": {
            bgcolor: "#fff",
          },
        }}
      >
        {active ? (
          <FavoriteIcon
            sx={{
              fontSize: {
                xs: 16,
                md: 18,
              },
            }}
            aria-hidden="true"
          />
        ) : (
          <FavoriteBorderIcon
            sx={{
              fontSize: {
                xs: 16,
                md: 18,
              },
            }}
            aria-hidden="true"
          />
        )}
      </Box>
    );
  }

  return (
    <Box
      role="img"
      aria-label={ariaLabel}
      sx={sx}
    >
      <FavoriteIcon
        sx={{
          fontSize: {
            xs: 16,
            md: 18,
          },
        }}
        aria-hidden="true"
      />
    </Box>
  );
}

/**
 * Story / article / video post.
 */
export function StoryCard({
  href,
  image,
  eyebrow,
  title,
  excerpt,
  ctaLabel,
  isVideo = false,
}) {
  return (
    <CardLink href={href}>
      <ArtworkFrame
        src={image}
        alt={title}
        ratio={isVideo ? "16 / 9" : "3 / 2"}
        mat={
          isVideo
            ? "#000000"
            : tokens.color.ivory
        }
        padding={
          isVideo
            ? 0
            : {
                xs: 1,
                md: 1.5,
              }
        }
      >
        {isVideo && <PlayBadge />}
      </ArtworkFrame>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1,
          pt: 2.5,
          flex: 1,
        }}
      >
        {eyebrow && (
          <Typography
            variant="overline"
            component="p"
          >
            {eyebrow}
          </Typography>
        )}

        <Typography
          className="lg-card-title"
          variant="h3"
          component="h3"
          sx={{
            color: tokens.color.ink,
            ...clamp(2),
          }}
        >
          {title}
        </Typography>

        {excerpt && (
          <Typography
            variant="body2"
            component="p"
            sx={clamp(3)}
          >
            {excerpt}
          </Typography>
        )}

        {ctaLabel && (
          <Typography
            component="span"
            sx={{
              mt: "auto",
              pt: 1,
              alignSelf: "flex-start",
              fontSize: "0.875rem",
              fontWeight: 600,
              letterSpacing: "0.06em",
              color: tokens.color.ink,
              borderBottom: `1px solid ${tokens.color.ink}`,
            }}
          >
            {ctaLabel}
          </Typography>
        )}
      </Box>
    </CardLink>
  );
}

function PlayBadge() {
  return (
    <Box
      aria-hidden="true"
      sx={{
        position: "absolute",

        left: "50%",
        top: "50%",

        transform: "translate(-50%, -50%)",

        width: 56,
        height: 56,

        borderRadius: "50%",

        bgcolor: "rgba(255,255,255,0.92)",

        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Box
        sx={{
          ml: "4px",

          width: 0,
          height: 0,

          borderTop: "10px solid transparent",
          borderBottom: "10px solid transparent",
          borderLeft: `16px solid ${tokens.color.ink}`,
        }}
      />
    </Box>
  );
}